/**
 * Vertical in-page navigation, as in Settings (cockatrice-ui `src/ui/SectionNav.jsx`, ported to hyperscript;
 * PLAN.md §0). items: [{ href, label, current?, badge? }]
 * or pass `current` (an href). On phones it becomes a sticky row of tabs that scrolls sideways.
 * An item with `onSelect` and no `href` is a button (PLAN.md §3, step 1: a page that moves within itself without
 * changing its address, as Settings does under the app's hash routes); it looks the same.
 */
import './css/section-nav.css';
import { mergeProps, splitProps } from 'solid-js';
import { each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface SectionNavItem {
  /** Link to the section (usually "#id"). */
  readonly href?: string | undefined;
  /** Instead of a link: what choosing the section does (scroll to it, move the focus). The item is then a button. */
  readonly onSelect?: (() => void) | undefined;
  /** Visible text. */
  readonly label: Child;
  /** Marks this section as current. */
  readonly current?: boolean | undefined;
  /** Small text on the right (desktop only). */
  readonly badge?: Child;
}

export interface SectionNavProps extends Attributes {
  /** The sections. */
  readonly items: readonly SectionNavItem[];
  /** Href of the current section. */
  readonly current?: string | undefined;
  /** Accessible name. Default "Sections". */
  readonly label?: string | undefined;
  /** Small heading above the list (desktop only). */
  readonly title?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function SectionNav(props: SectionNavProps): Child {
  const [local, rest] = splitProps(props, ['class', 'items', 'current', 'label', 'title']);
  const isCurrent = (it: SectionNavItem): boolean => it.current ?? (local.current !== undefined && local.current === it.href);
  const inside = (it: SectionNavItem): Child[] => [
    el('span', {}, () => it.label),
    when(
      () => it.badge !== undefined && it.badge !== null,
      () => el('span', { class: 'ui-section-nav__badge' }, () => it.badge),
    ),
  ];
  return el(
    'nav',
    mergeProps(
      {
        class: () => ['ui-section-nav', local.class].filter(Boolean).join(' '),
        'aria-label': () => local.label ?? uiText()('ui.section-nav.label'),
      },
      rest,
    ),
    when(
      () => Boolean(local.title),
      () => el('div', { class: 'ui-section-nav__title' }, () => local.title),
    ),
    el(
      'ul',
      { class: 'ui-section-nav__list' },
      each(
        () => local.items,
        (it) =>
          el(
            'li',
            {},
            it.onSelect !== undefined && it.href === undefined
              ? el(
                  'button',
                  {
                    type: 'button',
                    class: 'ui-section-nav__link',
                    'aria-current': () => (isCurrent(it) ? 'true' : undefined),
                    onClick: () => {
                      it.onSelect?.();
                    },
                  },
                  ...inside(it),
                )
              : el('a', { class: 'ui-section-nav__link', href: () => it.href, 'aria-current': () => (isCurrent(it) ? 'true' : undefined) }, ...inside(it)),
          ),
      ),
    ),
  );
}
